"use client";

import { Button } from "@/component/Button";
import { CloseOutlined } from "@ant-design/icons";
import Image from "next/image";
import Link from "next/link";
import { useCallback } from "react";
import logoIcon from "../../assets/logo-icon.png";
import discordImg from "../../assets/social-media-discord.png";
import googleImg from "../../assets/social-media-google.png";

interface EmailLoginFormProps {
  email: string;
  isLoading: boolean;
  onEmailChange: (email: string) => void;
  onEmailSubmit: () => void;
  onGoogleLogin: () => void;
  onDiscordLogin: () => void;
  onClose: () => void;
}

export function EmailLoginForm({
  email,
  isLoading,
  onEmailChange,
  onEmailSubmit,
  onGoogleLogin,
  onDiscordLogin,
  onClose,
}: EmailLoginFormProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        onEmailSubmit();
      }
    },
    [onEmailSubmit]
  );

  return (
    <div className="relative w-full sm:w-[480px] sm:rounded-[28px] sm:bg-background-card px-5 sm:px-[60px] py-[60px] min-w-[360px]">
      {/* 关闭按钮 */}
      <CloseOutlined
        className="absolute text-white! top-5 right-5 w-6 h-6 flex items-center justify-center bg-transparent border-none cursor-pointer opacity-70"
        onClick={onClose}
      />

      <div className="flex items-center justify-center w-20 h-20 mb-3 mx-auto rounded-full bg-text-primary">
        <Image src={logoIcon} alt="VidMuse" width={48} height={45} />
      </div>

      {/* Title */}
      <h1 className="my-8 text-center text-text-primary text-2xl font-bold">
        Sign in to VidMuse
      </h1>

      {/* Social login buttons */}
      <div className="flex flex-col gap-3">
        <Button
          type="plain"
          className="rounded-full text-text-primary! !font-medium bg-background-elevated hover:bg-background-elevated-2 active:bg-background-elevated-2 active:opacity-50"
          icon={<Image width={16} height={16} src={googleImg} alt="Google" />}
          onClick={onGoogleLogin}
        >
          Sign in with Google
        </Button>

        <Button
          type="plain"
          className="rounded-full text-text-primary! !font-medium bg-background-elevated hover:bg-background-elevated-2 active:bg-background-elevated-2 active:opacity-50"
          icon={<Image width={16} height={16} src={discordImg} alt="Discord" />}
          onClick={onDiscordLogin}
        >
          Sign in with Discord
        </Button>
      </div>

      {/* Divider */}
      <div className="relative my-7 h-[17px]">
        <div
          className="absolute top-1/2 -translate-y-1/2 w-full h-[1px]"
          style={{ background: "rgba(255, 255, 255, 0.10)" }}
        ></div>
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-sm text-text-secondary bg-background-card">
          or
        </span>
      </div>

      {/* Email input and submit button */}
      <div className="flex flex-col gap-4">
        <input
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Email"
          className="w-full h-12 px-4 rounded-full bg-background-elevated text-text-primary placeholder:text-text-tertiary placeholder:text-center text-center border border-transparent focus:border-primary focus:outline-none transition-colors"
          disabled={isLoading}
        />
        <Button
          type="primary"
          id="captcha-button-id"
          className="rounded-full !font-medium"
          onClick={onEmailSubmit}
          disabled={!email.trim() || isLoading}
        >
          {isLoading ? "Sending..." : "Send Verification Code"}
        </Button>
      </div>

      {/* Terms of Service */}
      <p className="mt-8 text-xs font-normal leading-normal text-text-secondary text-center w-full sm:w-[360px]">
        <span>By signing in, you are agreeing to the </span>
        <Link
          className="text-text-primary! font-medium underline! hover:text-primary!"
          href="/terms-of-service"
        >
          Terms of Service
        </Link>
        <span>, Acceptable </span>
        <Link
          className="text-text-primary! font-medium underline! hover:text-primary!"
          href="/use-policy"
        >
          Use Policy
        </Link>
        <span>, </span>
        <Link
          className="text-text-primary! font-medium underline! hover:text-primary!"
          href="/dispute-policy"
        >
          Dispute policy
        </Link>
        <span> and </span>
        <Link
          className="text-text-primary! font-medium underline! hover:text-primary!"
          href="/privacy-policy"
        >
          Privacy Policy.
        </Link>
      </p>
    </div>
  );
}
